import { useNavigate } from "@tanstack/react-router";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { useOrg } from "./OrgProvider";

/** An account's name, with "(shared with you)" when the person is not its Owner (accounts-places-sharing CL-08). */
export function accountLabel(m: { org_name: string; role: string }, sharedWithYou: string): string {
  return m.role === "owner" ? m.org_name : `${m.org_name} ${sharedWithYou}`;
}

/** "Creavision Technology" → "CT": the organisation in the top bar of a phone (web-on-phones WOP-02). */
export function orgInitials(name: string): string {
  const words = name.split(/\s+/).filter((w) => /\p{L}|\p{N}/u.test(w));
  return words
    .slice(0, 2)
    .map((w) => [...w.replace(/^[^\p{L}\p{N}]+/u, "")][0] ?? "")
    .join("")
    .toUpperCase();
}

const initialsBadge =
  "flex h-11 min-w-11 items-center justify-center rounded-md px-2 text-sm font-semibold text-gray-900 md:hidden";

export function OrgSwitcher() {
  const { t } = useTranslation();
  const { memberships, orgId, switchOrg } = useOrg();
  const id = useId();
  const navigate = useNavigate();
  if (memberships.length === 0) return null;
  // With one organisation there is nothing to switch to: its name only, never a control that looks like a menu
  // (alpha bug #3).
  const [only] = memberships;
  if (memberships.length === 1 && only)
    return (
      <>
        <span
          className="hidden max-w-56 truncate px-2 py-1.5 text-sm text-gray-900 md:inline"
          aria-label={t("org.label")}
        >
          {accountLabel(only, t("org.sharedWithYou"))}
        </span>
        <span className={initialsBadge} title={only.org_name}>
          <span aria-hidden="true">{orgInitials(only.org_name)}</span>
          <span className="sr-only">{only.org_name}</span>
        </span>
      </>
    );
  const current = memberships.find((m) => m.org_id === orgId);
  return (
    <div className="relative flex items-center gap-2">
      <label htmlFor={id} className="sr-only">
        {t("org.label")}
      </label>
      <select
        id={id}
        value={orgId}
        onChange={(e) => {
          // Another organisation opens on its home page (specs/home-page HOME-02).
          switchOrg(e.target.value);
          void navigate({ to: "/" });
        }}
        className="peer absolute inset-0 max-w-56 truncate rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm opacity-0 md:static md:opacity-100"
      >
        {memberships.map((m) => (
          <option key={m.org_id} value={m.org_id}>
            {accountLabel(m, t("org.sharedWithYou"))}
          </option>
        ))}
      </select>
      {/* Below 768 px the organisation shows as its initials over the list, which still opens on a tap (WOP-02). */}
      <span
        aria-hidden="true"
        className={`${initialsBadge} pointer-events-none border border-gray-300 bg-white peer-focus-visible:outline-2 peer-focus-visible:outline-brand`}
      >
        {orgInitials(current?.org_name ?? "")}
      </span>
    </div>
  );
}
